import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { getDashboardStats } from '../api/dashboard.api';
import { SeverityBadge } from '../components/SeverityBadge';
import { Spinner } from '../components/Spinner';
import type { Severity } from '@ixam/shared';

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: getDashboardStats,
    refetchInterval: 15_000,
  });

  return (
    <div className="p-5 md:p-10 max-w-6xl mx-auto space-y-8">
      <header>
        <h1 className="font-bold text-3xl text-ink">Panoramica</h1>
        <p className="text-sm text-ink-muted mt-1">
          Stato della flotta server e attività degli ultimi giorni.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="flex items-center gap-3 text-ink-muted">
          <Spinner size={20} /> Caricamento dati…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="Server monitorati" value={data.servers.total} subtitle="totali">
            <div className="grid grid-cols-2 gap-2 mt-5">
              <Row label="OK" value={data.servers.byHealth.ok ?? 0} dot="bg-sev-ok" />
              <Row
                label="Attenzione"
                value={data.servers.byHealth.warning ?? 0}
                dot="bg-sev-warning"
              />
              <Row
                label="Critico"
                value={data.servers.byHealth.critical ?? 0}
                dot="bg-sev-critical"
              />
              <Row
                label="Sconosciuto"
                value={data.servers.byHealth.unknown ?? 0}
                dot="bg-ink-faint"
              />
            </div>
          </Card>

          <Card title="Eventi · ultime 24h" value={data.events24h.total} subtitle="totali">
            <div className="mt-5 flex flex-wrap gap-3">
              {(['critical', 'warning', 'info', 'ok'] as Severity[]).map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <SeverityBadge severity={s} />
                  <span className="text-sm text-ink font-mono tabular-nums">
                    {data.events24h.bySeverity[s] ?? 0}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <p className="text-xs text-ink-faint">
        I dati si aggiornano automaticamente ogni 15 secondi.
      </p>
    </div>
  );
}

function Card({
  title,
  value,
  subtitle,
  children,
}: {
  title: string;
  value: number;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <div className="ixam-card p-6">
      <div className="text-sm text-ink-muted">{title}</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="font-extrabold text-5xl text-ink tabular-nums leading-none">{value}</div>
        {subtitle && <div className="text-xs text-ink-faint">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, dot }: { label: string; value: number; dot: string }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-paper-soft rounded-lg">
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        {label}
      </div>
      <span className="font-mono tabular-nums text-sm text-ink">{value}</span>
    </div>
  );
}
